// The 47 counties of Kenya, in alphabetical order, each with its county logo.
//
// Logos live in `public/counties/<slug>.webp` (served from `/counties/...`).
// Names follow the KEMSA county-logo sheet. This file is the single source of
// truth for the county list, so the seller side (where a listing's `city` is
// chosen) should import COUNTIES from here too. That way the value saved on a
// listing is always exactly one of these names.

export interface County {
  id: number;
  name: string;
  slug: string;
  logo: string;
  // Second place to look if `logo` 404s (e.g. the files were uploaded to the
  // site root instead of public/counties/).
  logoFallback: string;
}

export const ALL_COUNTIES_LABEL = "All Counties";

const RAW_COUNTIES: [string, string][] = [
  ["Baringo", "baringo"],
  ["Bomet", "bomet"],
  ["Bungoma", "bungoma"],
  ["Busia", "busia"],
  ["Elgeyo Marakwet", "elgeyo-marakwet"],
  ["Embu", "embu"],
  ["Garissa", "garissa"],
  ["Homabay", "homabay"],
  ["Isiolo", "isiolo"],
  ["Kajiado", "kajiado"],
  ["Kakamega", "kakamega"],
  ["Kericho", "kericho"],
  ["Kiambu", "kiambu"],
  ["Kilifi", "kilifi"],
  ["Kirinyaga", "kirinyaga"],
  ["Kisii", "kisii"],
  ["Kisumu", "kisumu"],
  ["Kitui", "kitui"],
  ["Kwale", "kwale"],
  ["Laikipia", "laikipia"],
  ["Lamu", "lamu"],
  ["Machakos", "machakos"],
  ["Makueni", "makueni"],
  ["Mandera", "mandera"],
  ["Marsabit", "marsabit"],
  ["Meru", "meru"],
  ["Migori", "migori"],
  ["Mombasa", "mombasa"],
  ["Murang’a", "muranga"],
  ["Nairobi City", "nairobi-city"],
  ["Nakuru", "nakuru"],
  ["Nandi", "nandi"],
  ["Narok", "narok"],
  ["Nyamira", "nyamira"],
  ["Nyandarua", "nyandarua"],
  ["Nyeri", "nyeri"],
  ["Samburu", "samburu"],
  ["Siaya", "siaya"],
  ["Taita Taveta", "taita-taveta"],
  ["Tana River", "tana-river"],
  ["Tharaka Nithi", "tharaka-nithi"],
  ["Trans Nzoia", "trans-nzoia"],
  ["Turkana", "turkana"],
  ["Uasin Gishu", "uasin-gishu"],
  ["Vihiga", "vihiga"],
  ["Wajir", "wajir"],
  ["West Pokot", "west-pokot"],
];

export const COUNTIES: County[] = RAW_COUNTIES.map(([name, slug], i) => ({
  id: i + 1,
  name,
  slug,
  logo: `/counties/${slug}.webp`,
  logoFallback: `/${slug}.webp`,
}));

export function getCountyByName(name: string): County | undefined {
  return COUNTIES.find((c) => c.name === name);
}

// "Nairobi", "Nairobi City", "Nairobi County", "nairobi" -> "nairobi"
// "Homa Bay" / "Homabay", "Trans-Nzoia" / "Trans Nzoia" all collapse the same way.
export function normalizeCountyName(value: string | null | undefined): string {
  return (value || "")
    .toLowerCase()
    .replace(/[\u2019'`]/g, "")
    .replace(/[^a-z]/g, "")
    .replace(/(county|city)$/, "");
}

// Listings created before the county switch may still carry an old city value.
// Map those onto the county they belong to so they don't silently disappear.
const LEGACY_CITY_TO_COUNTY: Record<string, string> = {
  eldoret: "uasingishu",
};

// Does a listing's stored `city` belong to the selected county?
export function listingMatchesCounty(
  listingCity: string | null | undefined,
  selectedCounty: string
): boolean {
  if (selectedCounty === ALL_COUNTIES_LABEL) return true;
  const listing = normalizeCountyName(listingCity);
  const wanted = normalizeCountyName(selectedCounty);
  if (!listing) return false;
  return (LEGACY_CITY_TO_COUNTY[listing] ?? listing) === wanted;
}

// Which county does a listing belong to? Newer sellers pick it in the
// application (seller_applications.county). Older approved sellers don't have
// that yet, so fall back to the county part of their "Area, County" location,
// and finally to the listing's own `city`.
export function resolveListingCounty(
  listing: any,
  sellerApp: any
): string {
  if (sellerApp?.county) return String(sellerApp.county);
  const loc = String(sellerApp?.location || "");
  if (loc.includes(",")) return loc.split(",").pop()!.trim();
  return String(listing?.city || "");
}
